// ── ¿Ha guardado el servidor lo que le hemos mandado? ─────────────────────
// CONTEXTO (verificado en producción el 01/10/2026: lectura + una prueba
// controlada y reversible, los dos trabajadores quedaron como estaban).
//
// PUT /api/company/person/:id responde 200 OK AUNQUE IGNORE campos del body.
// El caso probado es `isLoginEnabled`: en companyRouter.js la asignación vive
// DENTRO de `if (company && company.licenses)`, así que si la empresa NO tiene
// límite de licencias (licenses = 0 — 17 de las 25 empresas de producción) el
// campo no se asigna NUNCA. Medido: empresa "TemControl Demo" (licencias 6) →
// la baja se guarda; "ASOCIACIÓN PAZ Y BIEN" (licencias 0) → NO se guarda.
// Con límite hay un segundo caso: ACTIVAR a alguien cuando ya se ha alcanzado
// el tope también se ignora en silencio (el servidor solo saca un console.warn).
// `wantsGPSemails` arrastra la misma fama (ver services/api.js) y además el PUT
// ni lo devuelve en su respuesta, así que tampoco se puede dar por bueno.
//
// El backend es del compañero y NO se toca. Lo único honrado que puede hacer el
// panel es RELEER la ficha después de guardar y avisar de lo que no ha cuajado:
// nunca un "guardado" en verde mientras el trabajador sigue activo. Aquí NO se
// reintenta nada ni se toca la empresa: solo se informa.
//
// Módulo PURO (sin React y sin red: la relectura se inyecta). Testeado en
// src/utils/__tests__/workerSaveCheck.test.js.
// Gemelo en la app: TemControlApp/src/lib/workerSaveCheck.js — si se cambia una
// regla aquí, cambiarla allí (no está bajo el guard de check:shared porque cada
// repo lo coloca en su carpeta de lógica pura).

// Los flags del backend viajan como boolean, 0/1 o "true"/"false" según el
// endpoint y el driver. null = no se puede interpretar (y por tanto no se
// puede comparar: eso NO es un campo ignorado).
export const normalizeBool = (value) => {
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (v === 'true' || v === '1') return true;
    if (v === 'false' || v === '0') return false;
  }
  return null;
};

const quoted = (name) => (name ? `«${name}»` : '');

const licenseCap = (company) => {
  const n = Number(company?.licenses);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

// Causa CONOCIDA de que el servidor ignore `isLoginEnabled`. Devuelve null
// cuando no encaja con ninguna de las dos: no inventamos explicaciones.
const loginEnabledCause = ({ company, sent } = {}) => {
  // Sin la ficha de la empresa delante NO afirmamos cuál de los dos casos es:
  // decir "licencias = 0" sin haberlo leído sería otra mentira más.
  if (!company || typeof company !== 'object') {
    return 'El servidor SOLO guarda este campo cuando la empresa tiene un límite de licencias y '
      + 'queda sitio dentro de él. Revisa ese límite en la ficha de la empresa (si está en 0, este '
      + 'campo no se guarda nunca), o espera a que se corrija el backend.';
  }
  const cap = licenseCap(company);
  const who = company.name ? `La empresa ${quoted(company.name)}` : 'La empresa';
  if (cap === 0) {
    return `${who} no tiene límite de licencias (licencias = 0) y el servidor SOLO guarda este campo `
      + 'cuando la empresa tiene un límite. Pide a un administrador que le ponga un límite (generoso) '
      + 'en la ficha de la empresa, o espera a que se corrija el backend. Desde aquí no se toca nada '
      + 'de la empresa.';
  }
  if (sent === true) {
    return `${who} tiene ${cap} licencia${cap === 1 ? '' : 's'} y el servidor no activa a nadie más `
      + 'cuando ya se ha llegado al tope. Pide a un administrador que amplíe el límite en la ficha de '
      + 'la empresa, o desactiva antes a otro trabajador.';
  }
  return null;
};

// ── Campos que se verifican ───────────────────────────────────────────────
// Añadir uno nuevo es UNA línea más en esta lista.
//   key        → nombre del campo en el payload y en la ficha releída.
//   label      → cómo se llama en castellano dentro del aviso.
//   read       → normalizador (valor → comparable, o null si no se entiende).
//   stateText  → cómo se dice el estado REAL que ha quedado en el servidor.
//   cause      → ({ company, sent }) → frase accionable, o null si no se sabe.
export const VERIFIED_WORKER_FIELDS = [
  {
    key: 'isLoginEnabled',
    label: 'el estado activo/inactivo',
    read: normalizeBool,
    stateText: (v) => (v ? 'ACTIVO' : 'INACTIVO'),
    cause: loginEnabledCause,
  },
  {
    key: 'wantsGPSemails',
    label: 'el aviso por email de incidencias de GPS',
    read: normalizeBool,
    stateText: (v) => (v ? 'ACTIVADO' : 'DESACTIVADO'),
  },
];

const has = (obj, key) => !!obj && typeof obj === 'object'
  && Object.prototype.hasOwnProperty.call(obj, key) && obj[key] !== undefined;

// ── Comparación: lo que se mandó vs lo que ha quedado ─────────────────────
// Devuelve tres listas:
//   · saved         → se mandó y el servidor lo tiene igual. Perfecto.
//   · ignored       → se mandó y el servidor tiene OTRA cosa. Esto es el bug.
//   · unverifiable  → se mandó pero la ficha releída no trae el campo (o trae
//                     algo que no se puede interpretar), así que NO se puede
//                     afirmar que se haya ignorado. Se informa aparte, en voz
//                     baja: dar por "no guardado" lo que no se ha podido leer
//                     sería tan mentira como el 200 OK del servidor.
// Un campo que NO se mandó no se mira: no hay nada que comprobar.
export const compareSavedFields = (sent, fresh, fields = VERIFIED_WORKER_FIELDS) => {
  const result = { saved: [], ignored: [], unverifiable: [] };
  const list = Array.isArray(fields) ? fields : [];

  for (const field of list) {
    if (!field || !field.key) continue;
    if (!has(sent, field.key)) continue;

    const read = typeof field.read === 'function' ? field.read : (v) => v;
    const wanted = read(sent[field.key]);
    // Lo que mandamos no se puede ni interpretar: no es cosa del servidor.
    if (wanted === null || wanted === undefined) continue;

    if (!has(fresh, field.key)) {
      result.unverifiable.push({ key: field.key, label: field.label, reason: 'absent' });
      continue;
    }
    const actual = read(fresh[field.key]);
    if (actual === null || actual === undefined) {
      result.unverifiable.push({ key: field.key, label: field.label, reason: 'unreadable' });
      continue;
    }
    if (actual === wanted) {
      result.saved.push({ key: field.key, label: field.label, value: actual });
      continue;
    }
    const stateText = typeof field.stateText === 'function' ? field.stateText : (v) => String(v);
    result.ignored.push({
      key: field.key,
      label: field.label,
      sent: wanted,
      actual,
      actualText: stateText(actual),
      sentText: stateText(wanted),
      cause: typeof field.cause === 'function' ? field.cause : null,
    });
  }

  return result;
};

const joinLabels = (labels) => {
  const l = labels.filter(Boolean);
  if (!l.length) return '';
  if (l.length === 1) return l[0];
  return `${l.slice(0, -1).join(', ')} y ${l[l.length - 1]}`;
};

const savedHeadline = ({ workerName, created }) => (
  `Trabajador${workerName ? ` ${quoted(workerName)}` : ''} ${created ? 'creado' : 'actualizado'}`
);

const who = (workerName) => workerName || 'el trabajador';

// ── Texto del aviso ───────────────────────────────────────────────────────
// Una sola cadena (el toast de la web y el Alert de la app la pintan tal
// cual). Orden: qué sí se guardó → qué NO → por qué y qué hacer → el resto
// sigue bien. '' cuando no hay nada que avisar.
export const workerSaveWarningText = ({
  workerName, created = false, ignored = [], unverifiable = [], company, readError,
} = {}) => {
  const parts = [];

  if (readError) {
    parts.push(`${savedHeadline({ workerName, created })}, pero NO se ha podido COMPROBAR que el `
      + `servidor haya guardado todos los campos${readError.message ? ` (${readError.message})` : ''}.`);
    parts.push(`Revisa la ficha de ${who(workerName)}: el servidor responde "guardado" aunque ignore `
      + 'en silencio el estado activo/inactivo.');
    return parts.join(' ');
  }

  if (!ignored.length && !unverifiable.length) return '';

  if (ignored.length) {
    parts.push(`${savedHeadline({ workerName, created })}, pero el servidor NO lo ha guardado todo.`);
    for (const f of ignored) {
      parts.push(`El servidor NO ha guardado ${f.label} de ${who(workerName)}. Sigue como ${f.actualText}.`);
      const cause = f.cause ? f.cause({ company, sent: f.sent, actual: f.actual, workerName }) : null;
      if (cause) parts.push(cause);
    }
    parts.push('El resto de los cambios sí se han guardado.');
  } else {
    parts.push(`${savedHeadline({ workerName, created })}.`);
  }

  if (unverifiable.length) {
    parts.push(`La ficha releída no trae ${joinLabels(unverifiable.map((f) => f.label))}, así que eso `
      + 'no se ha podido comprobar: míralo en su ficha.');
  }

  return parts.join(' ');
};

// Ficha fresca dentro de una lista de trabajadores. En EDICIÓN basta el id;
// en ALTA el POST /api/company/person NO devuelve id, así que se busca por
// email (sin distinguir mayúsculas) y, si no, por NIF — el mismo criterio que
// usa BulkImportModal para resolver el id de los recién creados.
export const findSavedWorker = (list, { id, email, nif } = {}) => {
  const workers = Array.isArray(list) ? list : [];
  const norm = (v) => String(v == null ? '' : v).trim().toLowerCase();
  if (id != null && id !== '') {
    const byId = workers.find((w) => w && String(w.id) === String(id));
    if (byId) return byId;
  }
  if (email) {
    const byEmail = workers.find((w) => w && norm(w.email) === norm(email));
    if (byEmail) return byEmail;
  }
  if (nif) {
    const byNif = workers.find((w) => w && norm(w.nif) === norm(nif));
    if (byNif) return byNif;
  }
  return null;
};

// ── Verificación completa ─────────────────────────────────────────────────
// 1. RELEE la ficha (readWorker inyectado — nada de red aquí dentro).
// 2. Compara campo a campo lo que se mandó con lo que ha quedado.
// 3. Devuelve nivel + texto listo para pintar.
//
// NUNCA lanza ni bloquea el guardado: si la relectura falla (o no devuelve la
// ficha) el nivel es 'warning' y el mensaje dice exactamente eso — que el
// guardado no se ha podido verificar.
//
//   level === 'success' → no hay nada que avisar; el que llama pinta su
//                         mensaje de éxito de siempre.
//   level === 'warning' → pinta `message` (y en la web, sin auto-cierre: el
//                         admin TIENE que leerlo).
export const verifyWorkerSave = async ({
  workerName, created = false, sent, company, readWorker, fields = VERIFIED_WORKER_FIELDS,
} = {}) => {
  const base = {
    level: 'success', message: '', verified: false,
    ignored: [], unverifiable: [], saved: [], readError: null,
  };

  // Nada comparable en el payload → nada que comprobar.
  const anySent = (Array.isArray(fields) ? fields : []).some((f) => f && f.key && has(sent, f.key));
  if (!anySent) return { ...base, verified: true };

  let fresh = null;
  let readError = null;
  if (typeof readWorker === 'function') {
    try {
      fresh = await readWorker();
    } catch (err) {
      readError = err || new Error('relectura fallida');
    }
  } else {
    readError = new Error('sin relectura disponible');
  }
  if (!readError && (!fresh || typeof fresh !== 'object')) {
    readError = new Error('el servidor no ha devuelto la ficha');
  }

  if (readError) {
    return {
      ...base,
      level: 'warning',
      readError,
      message: workerSaveWarningText({ workerName, created, company, readError }),
    };
  }

  const cmp = compareSavedFields(sent, fresh, fields);
  const message = workerSaveWarningText({
    workerName, created, company, ignored: cmp.ignored, unverifiable: cmp.unverifiable,
  });

  return {
    ...base,
    level: message ? 'warning' : 'success',
    verified: true,
    saved: cmp.saved,
    ignored: cmp.ignored,
    unverifiable: cmp.unverifiable,
    message,
  };
};
